import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Scale, Calendar, ChevronRight, ArrowLeft } from "lucide-react";
import { PaginationControls } from "../../components/ui/PaginationControls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MyDisputes = ({ user, showToast }) => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchMyDisputes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/disputes/my`);
      setDisputes(response.data);
    } catch (error) {
      console.error("Error fetching disputes:", error);
      showToast("Ошибка при загрузке споров", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyDisputes();
  }, [fetchMyDisputes]);

  const getStatusInfo = (dispute) => {
    const statusMap = {
      pending: { text: "В ожидании", color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
      auto_resolved: { text: "Решён автоматически", color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
      manual_review: { text: "На рассмотрении", color: 'var(--rv-teal)', bg: 'rgba(13,148,136,0.1)' },
      resolved: { text: "Решён", color: '#10b981', bg: 'rgba(16,185,129,0.1)' }
    };
    const status = statusMap[dispute.status] || { text: dispute.status, color: 'var(--rv-text-muted)', bg: 'rgba(107,114,128,0.1)' };
    let winnerText = "";
    if (dispute.resolved_in_favor_of) {
      if (dispute.resolved_in_favor_of === user.id) {
        winnerText = " — в вашу пользу";
        return { ...status, text: status.text + winnerText, color: '#10b981', bg: 'rgba(16,185,129,0.1)' };
      } else {
        winnerText = " — не в вашу пользу";
        return { ...status, text: status.text + winnerText, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
      }
    }
    return { ...status, text: status.text + winnerText };
  };

  const getReasonText = (reason) => {
    const reasonMap = {
      quality_issues: "Проблемы с качеством",
      missing_files: "Недостающие файлы",
      wrong_content: "Неправильное содержание",
      technical_issues: "Технические проблемы",
      deadline_missed: "Нарушение срока",
      other: "Другое"
    };
    return reasonMap[reason] || reason;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
        <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка споров...</p>
      </div>
    );
  }

  if (selectedDispute) {
    return (
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedDispute(null)}
            className="flex items-center gap-1.5 text-sm"
            style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer'}}
          >
            <ArrowLeft size={15}/>Назад к спорам
          </button>
          <h2 className="text-xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Детали спора</h2>
        </div>

        <div className="reversia-card p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>{selectedDispute.task_title}</h3>
            <div className="flex items-center gap-4 text-sm" style={{color:'var(--rv-text-muted)'}}>
              <span className="font-medium" style={{color:'#10b981'}}>{selectedDispute.task_price} ₽</span>
              <span className="flex items-center gap-1"><Calendar size={13}/>{new Date(selectedDispute.created_at).toLocaleDateString('ru-RU')}</span>
            </div>
          </div>

          {(() => {
            const si = getStatusInfo(selectedDispute);
            return (
              <div className="inline-block px-3 py-1 rounded-full text-sm mb-4 font-medium" style={{color:si.color, background:si.bg}}>
                {si.text}
              </div>
            );
          })()}

          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2 text-sm" style={{color:'var(--rv-text-primary)'}}>Причина спора:</h4>
              <p style={{color:'var(--rv-text-muted)'}}>{getReasonText(selectedDispute.reason)}</p>
            </div>

            <div>
              <h4 className="font-medium mb-2 text-sm" style={{color:'var(--rv-text-primary)'}}>Описание проблемы:</h4>
              <p className="whitespace-pre-wrap" style={{color:'var(--rv-text-muted)'}}>{selectedDispute.description}</p>
            </div>

            {selectedDispute.evidence_files && selectedDispute.evidence_files.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-sm" style={{color:'var(--rv-text-primary)'}}>Доказательства:</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {selectedDispute.evidence_files.map((evidence, index) => (
                    <img
                      key={index}
                      src={evidence}
                      alt={`Доказательство ${index + 1}`}
                      className="w-full h-48 object-cover rounded cursor-pointer hover:opacity-75"
                      style={{border:'1px solid var(--rv-border)'}}
                      onClick={() => setSelectedEvidence(evidence)}
                    />
                  ))}
                </div>
              </div>
            )}

            {selectedDispute.admin_notes && (
              <div>
                <h4 className="font-medium mb-2 text-sm" style={{color:'var(--rv-text-primary)'}}>Комментарий администратора:</h4>
                <p className="p-3 rounded" style={{color:'var(--rv-text-muted)', background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>{selectedDispute.admin_notes}</p>
              </div>
            )}
          </div>
        </div>

        {selectedEvidence && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4"
            style={{zIndex:9000}}
            onClick={() => setSelectedEvidence(null)}
          >
            <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-t-lg px-4 py-2">
                <h3 className="text-white text-sm font-medium">Доказательство</h3>
                <button onClick={() => setSelectedEvidence(null)} className="text-white text-2xl font-bold leading-none w-8 h-8 flex items-center justify-center" style={{background:'none',border:'none',cursor:'pointer'}}>×</button>
              </div>
              <div className="flex items-center justify-center overflow-hidden rounded-b-lg bg-black">
                <img src={selectedEvidence} alt="Доказательство" className="max-w-full max-h-[80vh] object-contain" draggable={false}/>
              </div>
              <p className="text-center text-gray-400 text-xs mt-2">Нажмите за пределами фото для закрытия</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Мои споры</h2>
      
      {disputes.length === 0 ? (
        <div className="text-center py-12">
          <Scale size={40} className="mx-auto mb-4" style={{color:'var(--rv-text-muted)'}}/>
          <p style={{color:'var(--rv-text-muted)'}}>У вас пока нет споров</p>
          <p className="text-sm mt-2" style={{color:'var(--rv-text-muted)'}}>
            Споры появляются, когда возникают разногласия по выполнению заданий
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {disputes.slice((currentPage - 1) * perPage, currentPage * perPage).map((dispute) => {
            const statusInfo = getStatusInfo(dispute);
            return (
              <div key={dispute.id} className="reversia-card p-4">
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold leading-tight mb-1" style={{color:'var(--rv-text-primary)', overflowWrap:'anywhere', wordBreak:'break-word'}}>
                      {dispute.task_title}
                    </h3>
                    <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>{getReasonText(dispute.reason)}</p>
                  </div>
                  <div className="text-right flex-shrink-0 max-w-[130px]">
                    <div className="text-base font-bold whitespace-nowrap" style={{color:'#10b981'}}>{dispute.task_price} ₽</div>
                    <div className="text-xs px-2 py-1 rounded-full mt-1 leading-tight" style={{color:statusInfo.color, background:statusInfo.bg}}>
                      {statusInfo.text}
                    </div>
                  </div>
                </div>

                <p className="text-sm mb-4 line-clamp-2" style={{color:'var(--rv-text-muted)'}}>
                  {dispute.description}
                </p>

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="flex items-center gap-1 text-sm" style={{color:'var(--rv-text-muted)'}}>
                    <Calendar size={13}/>{new Date(dispute.created_at).toLocaleDateString('ru-RU')}
                  </span>
                  <button
                    onClick={() => setSelectedDispute(dispute)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium"
                    style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                  >
                    Подробности<ChevronRight size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
          </div>
          <PaginationControls
            total={disputes.length}
            perPage={perPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onPerPageChange={setPerPage}
          />
        </>
      )}
    </div>
  );
};

